// src/components/seating-chart/user/seatAssignment.js  (NEW FILE)
//
// Pure functions, no React. This is the replacement for the old "ticket type
// name === seats.io category label" rule.
//
// Vocabulary
//   budget       { [ticketTypeId]: quantity }   what the buyer chose in the ticket step
//   eligibility  Map<categoryId, ticketTypeId[]>  built from ticketType.seatingChartCategories
//   seat         { seatId, categoryId }
//
// Why a matching instead of "pick the first eligible ticket type"?
//   Adult -> {Stalls, Balcony}, Child -> {Stalls}, budget = 1 Adult + 1 Child.
//   Click a Stalls seat first: greedy might give it "Adult", and then the Balcony
//   seat has nobody left to take it although Stalls=Child, Balcony=Adult works.
//   Because price depends on ticket type (not on the seat) the buyer never needs
//   to know which seat got which type - we just have to find ANY valid assignment.

export const buildEligibility = (ticketTypes) => {
  const map = new Map();
  [...(ticketTypes ?? [])]
    .filter((tt) => tt.isPublished !== false)
    .sort((a, b) => (a.ticketOrder ?? 0) - (b.ticketOrder ?? 0))
    .forEach((tt) => {
      (tt.seatingChartCategories ?? []).forEach((c) => {
        map.set(c.id, [...(map.get(c.id) ?? []), tt.id]);
      });
    });
  return map;
};

export const sumBudget = (budget) => Object.values(budget).reduce((a, b) => a + (b || 0), 0);

/**
 * Capacitated bipartite matching (Kuhn / augmenting paths). Selections are tiny
 * (<= ~20 seats) so this is instant.
 * @returns {{ ok: boolean, assignment: Record<string,string> }} seatId -> ticketTypeId
 */
export const assignTicketTypes = (seats, budget, eligibility) => {
  const seatById = Object.fromEntries(seats.map((s) => [s.seatId, s]));
  const assigned = {}; // ticketTypeId -> seatId[]
  const matchOf = {}; // seatId -> ticketTypeId

  const place = (seatId, t) => {
    (assigned[t] ??= []).push(seatId);
    matchOf[seatId] = t;
  };
  const unplace = (seatId, t) => {
    assigned[t] = assigned[t].filter((id) => id !== seatId);
    delete matchOf[seatId];
  };

  const augment = (seat, visited) => {
    for (const t of eligibility.get(seat.categoryId) ?? []) {
      if (visited.has(t)) continue;
      visited.add(t);
      if ((assigned[t]?.length ?? 0) < (budget[t] ?? 0)) {
        place(seat.seatId, t);
        return true;
      }
      for (const otherId of [...(assigned[t] ?? [])]) {
        unplace(otherId, t);
        if (augment(seatById[otherId], visited)) {
          place(seat.seatId, t);
          return true;
        }
        place(otherId, t); // couldn't move it - put it back
      }
    }
    return false;
  };

  for (const seat of seats) {
    if (!augment(seat, new Set())) return { ok: false, assignment: matchOf };
  }
  return { ok: true, assignment: matchOf };
};

/**
 * Decide what happens when the buyer clicks a free seat.
 *
 * @param {object}  p
 * @param {Array}   p.selected            [{seatId, categoryId}] already selected
 * @param {object}  p.seat                {seatId, categoryId}
 * @param {object}  p.budget              current budget
 * @param {Map}     p.eligibility
 * @param {object}  p.ticketTypesById     id -> ticketType ({available, ...})
 * @param {string}  p.autoIncreaseWhen    'full' (spec: only once every ticket has a seat) | 'always'
 * @param {number}  p.maxTotalTickets     cap on total tickets (cart limit), Infinity if none
 * @param {number}  p.otherCartTickets    tickets of this event already in the cart outside this budget
 *
 * @returns one of
 *   { kind: 'ok', assignment }
 *   { kind: 'increase', ticketTypeId, budget, assignment }   -> auto +1 ticket
 *   { kind: 'choose', options: [ticketTypeId] }               -> several types fit, let the buyer pick
 *   { kind: 'blocked', reason }  NO_TICKET_TYPE | NOT_FULL | SOLD_OUT | MAX_TICKETS
 */
export const planSeatAdd = ({
  selected,
  seat,
  budget,
  eligibility,
  ticketTypesById,
  autoIncreaseWhen = "always",
  maxTotalTickets = Infinity,
  otherCartTickets = 0,
}) => {
  const next = [...selected, seat];
  const direct = assignTicketTypes(next, budget, eligibility);
  if (direct.ok) return { kind: "ok", assignment: direct.assignment };

  const typesForSeat = eligibility.get(seat.categoryId) ?? [];
  if (!typesForSeat.length) return { kind: "blocked", reason: "NO_TICKET_TYPE" };

  const total = sumBudget(budget);
  const isFull = selected.length >= total;
  if (autoIncreaseWhen === "full" && !isFull) return { kind: "blocked", reason: "NOT_FULL" };

  if (total + otherCartTickets + 1 > maxTotalTickets) return { kind: "blocked", reason: "MAX_TICKETS" };

  const candidates = typesForSeat.filter((t) => {
    const available = ticketTypesById[t]?.available;
    return available === undefined || available > (budget[t] ?? 0);
  });
  if (!candidates.length) return { kind: "blocked", reason: "SOLD_OUT" };

  // the spec'd auto case: the seat is linked to exactly ONE ticket type
  if (typesForSeat.length === 1) {
    const t = candidates[0];
    const newBudget = { ...budget, [t]: (budget[t] ?? 0) + 1 };
    const r = assignTicketTypes(next, newBudget, eligibility);
    if (!r.ok) return { kind: "blocked", reason: "NO_TICKET_TYPE" };
    return { kind: "increase", ticketTypeId: t, budget: newBudget, assignment: r.assignment };
  }

  return { kind: "choose", options: candidates };
};

/** Apply a "choose" answer: +1 on that type and re-run the matching. */
export const applyIncrease = ({ selected, seat, budget, eligibility, ticketTypeId }) => {
  const newBudget = { ...budget, [ticketTypeId]: (budget[ticketTypeId] ?? 0) + 1 };
  const r = assignTicketTypes([...selected, seat], newBudget, eligibility);
  return r.ok ? { ok: true, budget: newBudget, assignment: r.assignment } : { ok: false };
};

/**
 * After a seat is removed: if tickets were auto-added earlier and there is now a
 * free slot, take one auto-added ticket back (so de-selecting an "extra" seat
 * returns the order to what it was).
 */
export const planSeatRemove = ({ remaining, budget, autoAdded, eligibility }) => {
  let newBudget = budget;
  const newAuto = { ...autoAdded };

  if (remaining.length < sumBudget(budget)) {
    for (const t of Object.keys(newAuto)) {
      if (newAuto[t] <= 0 || (budget[t] ?? 0) <= 0) continue;
      const trial = { ...budget, [t]: budget[t] - 1 };
      if (assignTicketTypes(remaining, trial, eligibility).ok) {
        newBudget = trial;
        newAuto[t] -= 1;
        break;
      }
    }
  }
  const r = assignTicketTypes(remaining, newBudget, eligibility);
  return { budget: newBudget, autoAdded: newAuto, assignment: r.assignment };
};

/** Cart items in the exact shape the existing createCartItems / updateCartItem code expects. */
export const groupIntoCartItems = ({ selected, assignment, ticketTypesById, holdToken }) => {
  const byType = {};
  selected.forEach((s) => {
    const t = assignment[s.seatId];
    if (!t) return;
    (byType[t] ??= []).push(s.seatId);
  });
  return Object.entries(byType)
    .map(([ticketTypeId, seatIds]) => {
      const tt = ticketTypesById[ticketTypeId] ?? {};
      return {
        id: ticketTypeId,
        name: tt.name,
        price: tt.price,
        quantity: seatIds.length,
        seatIds,
        seatsHoldToken: holdToken,
      };
    })
    .sort((a, b) => (ticketTypesById[a.id]?.ticketOrder ?? 0) - (ticketTypesById[b.id]?.ticketOrder ?? 0));
};